import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  CheckCircle,
  Zap,
  Users,
  FileText,
  Clock,
  TrendingUp,
  ArrowRight,
  Briefcase,
  ListChecks,
  Unlock,
  ShieldCheck,
} from "lucide-react";

const STEPS = {
  CHOICE: 0,
  CHECKLIST: 1,
  TRIAL_UNLOCK: 2,
  CONSENT: 3,
};

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, agreeToTerms, isAdmin } = useAuth();
  const [step, setStep] = useState(STEPS.CHOICE);
  const [consentChecked, setConsentChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Admins skip onboarding
  useEffect(() => {
    if (isAdmin) {
      navigate("/dashboard");
    }
  }, [isAdmin, navigate]);

  const handleConsent = async () => {
    if (!consentChecked) return;
    setSubmitting(true);
    try {
      const ok = await agreeToTerms();
      if (ok) {
        toast.success("Welcome! Your free trial has started.");
        navigate("/dashboard");
      } else {
        toast.error("Failed to record consent. Please try again.");
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const checklistItems = [
    { icon: Users, label: "Clients & Projects workspace" },
    { icon: Clock, label: "Time tracking & billing" },
    { icon: FileText, label: "Professional invoices & quotes" },
    { icon: TrendingUp, label: "Dashboard analytics" },
    { icon: Zap, label: "Recurring invoices" },
    { icon: Briefcase, label: "Financial reports & savings tracker" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Step indicators */}
        <div className="flex justify-center gap-2 mb-8">
          {Object.values(STEPS).map((s) => (
            <div
              key={s}
              className={`h-2 rounded-full transition-all duration-300 ${
                s === step ? "w-8 bg-blue-400" : s < step ? "w-4 bg-blue-600" : "w-4 bg-gray-600"
              }`}
            />
          ))}
        </div>

        {/* Step 0: Starting choice screen */}
        {step === STEPS.CHOICE && (
          <div className="text-center space-y-6">
            <div>
              <h2 className="text-3xl font-bold text-white mb-2">
                What best describes you?
              </h2>
              <p className="text-gray-400">
                Help us tailor your experience from day one.
              </p>
            </div>
            <div className="space-y-3">
              {[
                { icon: "🧑‍💻", label: "Solo freelancer", sub: "I work independently with my own clients" },
                { icon: "🚀", label: "Starting out", sub: "I'm new to freelancing and building my client base" },
                { icon: "🏢", label: "Small agency / team", sub: "I manage projects with collaborators or staff" },
              ].map(({ icon, label, sub }) => (
                <button
                  key={label}
                  onClick={() => setStep(STEPS.CHECKLIST)}
                  className="w-full text-left p-4 rounded-xl bg-gray-800/50 border border-gray-700 hover:border-blue-500/50 hover:bg-gray-800 transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{icon}</span>
                    <div>
                      <p className="text-white font-medium group-hover:text-blue-300 transition-colors">
                        {label}
                      </p>
                      <p className="text-gray-400 text-sm">{sub}</p>
                    </div>
                    <ArrowRight className="ml-auto h-4 w-4 text-gray-600 group-hover:text-blue-400 transition-colors" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Workspace readiness checklist */}
        {step === STEPS.CHECKLIST && (
          <div className="text-center space-y-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/10 rounded-2xl mx-auto">
              <ListChecks className="h-8 w-8 text-green-400" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-white mb-2">
                Your workspace is ready
              </h2>
              <p className="text-gray-400">
                Everything you need is set up and waiting for you.
              </p>
            </div>
            <div className="space-y-3 text-left">
              {checklistItems.map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-3 p-3 rounded-lg bg-gray-800/40">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center">
                    <Icon className="h-4 w-4 text-green-400" />
                  </div>
                  <span className="text-gray-200 text-sm">{label}</span>
                  <CheckCircle className="ml-auto h-4 w-4 text-green-400 flex-shrink-0" />
                </div>
              ))}
            </div>
            <Button
              className="w-full"
              size="lg"
              onClick={() => setStep(STEPS.TRIAL_UNLOCK)}
            >
              Continue <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Step 2: Trial unlock screen */}
        {step === STEPS.TRIAL_UNLOCK && (
          <div className="text-center space-y-6">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-500/10 rounded-3xl mx-auto">
              <Unlock className="h-10 w-10 text-yellow-400" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-white mb-2">
                🎉 Trial unlocked!
              </h2>
              <p className="text-gray-400 text-lg">
                Your 10-day free trial has started. Explore everything
                FreelanceTrackIt has to offer — no credit card required.
              </p>
            </div>
            <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-5 space-y-3 text-left">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                <p className="text-gray-300 text-sm">
                  <strong className="text-white">Full access for 10 days</strong> — all features unlocked, no restrictions.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                <p className="text-gray-300 text-sm">
                  <strong className="text-white">₱699/month</strong> after your trial — cancel anytime, no questions asked.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                <p className="text-gray-300 text-sm">
                  <strong className="text-white">Secure payments via PayPal</strong> — your billing details are never stored on our servers.
                </p>
              </div>
            </div>
            <Button
              className="w-full"
              size="lg"
              onClick={() => setStep(STEPS.CONSENT)}
            >
              Review terms &amp; start <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Step 3: Terms & consent */}
        {step === STEPS.CONSENT && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/10 rounded-2xl mx-auto mb-4">
                <ShieldCheck className="h-8 w-8 text-blue-400" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">
                Terms &amp; consent
              </h2>
              <p className="text-gray-400">
                Please review before accessing your workspace.
              </p>
            </div>
            <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-5 space-y-3 text-sm text-gray-400 max-h-60 overflow-y-auto">
              <p>Welcome to FreelanceTrackIt! Here's a quick summary of your access and billing.</p>
              <p>
                <strong className="text-gray-200">Free 10-Day Trial:</strong> You get full workspace access for 10 days, no payment required.
              </p>
              <p>
                <strong className="text-gray-200">Subscription:</strong> After your free trial, continue with FreelanceTrackIt for ₱699/month. Subscriptions auto-renew monthly and you can cancel anytime.
              </p>
              <p>
                <strong className="text-gray-200">Auto-Archive:</strong> Accounts inactive for 180 days may be archived and lose dashboard access until restored.
              </p>
              <p>
                <strong className="text-gray-200">Privacy:</strong> We only collect the data needed to run your workspace and billing. See the Privacy Policy for details.
              </p>
              <p>
                <strong className="text-gray-200">Terms:</strong> By agreeing, you accept the Terms of Service and authorize recurring billing at ₱699/month after your trial ends.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <input
                id="onboardingConsent"
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                className="h-4 w-4 accent-blue-500"
              />
              <Label htmlFor="onboardingConsent" className="text-gray-300 cursor-pointer">
                I understand and agree to the terms above
              </Label>
            </div>
            <Button
              className="w-full"
              size="lg"
              disabled={!consentChecked || submitting}
              onClick={handleConsent}
            >
              {submitting ? "Starting your trial..." : "Start my free trial"}
              {!submitting && <ArrowRight className="ml-2 h-4 w-4" />}
            </Button>
            <p className="text-center text-gray-500 text-xs">
              Logged in as {user?.email}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
